import { SelectProps } from 'antd';
import React from 'react';
import ScopeConfigurationFieldRow from './ScopeConfigurationFieldRow';
import {
  ConfigurationRuleScopeEntityType,
  ConfigurationRuleScopeMode,
  ConfigurationRuleSetupType,
} from '@ydistri/api-sdk';
import { Column } from '@ydistri/ds';
import {
  isMainScopePriority,
  ScopeConfiguration,
  scopeEntityTitle,
  ViewMode,
} from './scopeUtilsLib';

export const FAKE_SCOPE_ID = 0;

export type ScopeConfigurationIconReplacement = 'cross' | 'check' | undefined;

export const configurationRuleScopeModeColor: Record<ConfigurationRuleScopeMode, string> = {
  [ConfigurationRuleScopeMode.All]: 'green',
  [ConfigurationRuleScopeMode.Specified]: 'blue',
  [ConfigurationRuleScopeMode.AllExcept]: 'gold',
};

export const configurationRuleScopeModeTitle: Record<ConfigurationRuleScopeMode, string> = {
  [ConfigurationRuleScopeMode.All]: 'All',
  [ConfigurationRuleScopeMode.Specified]: 'Specified',
  [ConfigurationRuleScopeMode.AllExcept]: 'Excluded',
};

export const getScopeTypeSelectOptions = (
  isPlayground: boolean,
): NonNullable<SelectProps<ConfigurationRuleScopeMode>['options']> => {
  return [
    {
      value: ConfigurationRuleScopeMode.All,
      label: `All${isPlayground ? '' : ' in general configuration'}`,
    },
    {
      value: ConfigurationRuleScopeMode.Specified,
      label: `Specify ${isPlayground ? '' : ' from general configuration'}`,
    },
    {
      value: ConfigurationRuleScopeMode.AllExcept,
      label: `Exclude ${isPlayground ? '' : ' from general configuration'}`,
    },
  ];
};

interface RenderUseAsSourceParams {
  scopeId: number;
  viewMode: ViewMode;
  disabled: boolean;
}

const renderUseAsSource = ({
  scopeId,
  viewMode,
  disabled,
}: RenderUseAsSourceParams): React.ReactNode => {
  return (
    <ScopeConfigurationFieldRow
      title="Use as source"
      scopeId={scopeId}
      viewMode={viewMode}
      field={ConfigurationRuleSetupType.UseAsSource}
      disabled={disabled}
    />
  );
};

const renderUseAsTarget = (
  scopeId: number,
  viewMode: ViewMode,
  disabled: boolean,
): React.ReactNode => {
  return (
    <ScopeConfigurationFieldRow
      title="Use as target"
      scopeId={scopeId}
      viewMode={viewMode}
      field={ConfigurationRuleSetupType.UseAsTarget}
      disabled={disabled}
    />
  );
};

interface GetScopeConfigurationColumns {
  scopeId: number;
  viewMode: ViewMode;
  disabled: boolean;
}

export interface ScopeConfigurationDefinition {
  key: ConfigurationRuleSetupType;
  title: string;
  render: (record: ScopeConfiguration) => React.ReactNode;
}

export type ScopeConfigurationDefinitions = Record<
  ConfigurationRuleSetupType,
  ScopeConfigurationDefinition
>;

export const getScopeConfigurationDefinitions = (
  params: GetScopeConfigurationColumns,
): ScopeConfigurationDefinitions => {
  const { scopeId, viewMode, disabled } = params;
  return {
    [ConfigurationRuleSetupType.UseAsSource]: {
      title: 'Use as source',
      key: ConfigurationRuleSetupType.UseAsSource,
      render: () =>
        renderUseAsSource({
          scopeId,
          viewMode,
          disabled,
        }),
    },
    [ConfigurationRuleSetupType.UseAsTarget]: {
      title: 'Use as target',
      key: ConfigurationRuleSetupType.UseAsTarget,
      render: () => renderUseAsTarget(scopeId, viewMode, disabled),
    },
    [ConfigurationRuleSetupType.MaxNumberOfSourceStores]: {
      title: 'Max. incoming routes',
      key: ConfigurationRuleSetupType.MaxNumberOfSourceStores,
      render: () => {
        return (
          <ScopeConfigurationFieldRow
            title="Max. incoming routes"
            scopeId={scopeId}
            viewMode={viewMode}
            field={ConfigurationRuleSetupType.MaxNumberOfSourceStores}
            disabled={disabled}
          />
        );
      },
    },
    [ConfigurationRuleSetupType.MaxNumberOfTargetStores]: {
      title: 'Max. outgoing routes',
      key: ConfigurationRuleSetupType.MaxNumberOfTargetStores,
      render: () => {
        return (
          <ScopeConfigurationFieldRow
            title="Max. outgoing routes"
            scopeId={scopeId}
            viewMode={viewMode}
            field={ConfigurationRuleSetupType.MaxNumberOfTargetStores}
            disabled={disabled}
          />
        );
      },
    },
    [ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions]: {
      key: ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions,
      title: 'Max. outgoing picking positions',
      render: () => {
        return (
          <ScopeConfigurationFieldRow
            title="Max. outgoing picking positions"
            scopeId={scopeId}
            viewMode={viewMode}
            field={ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions}
            disabled={disabled}
          />
        );
      },
    },
    [ConfigurationRuleSetupType.MinimumRouteValue]: {
      key: ConfigurationRuleSetupType.MinimumRouteValue,
      title: 'Minimum route value',
      render: () => {
        return (
          <ScopeConfigurationFieldRow
            title="Minimum route value"
            viewMode={viewMode}
            scopeId={scopeId}
            field={ConfigurationRuleSetupType.MinimumRouteValue}
            disabled={disabled}
          />
        );
      },
    },
    [ConfigurationRuleSetupType.IsEnabled]: {
      key: ConfigurationRuleSetupType.IsEnabled,
      title: 'Enabled',
      render: () => {
        return (
          <ScopeConfigurationFieldRow
            title="Enabled"
            viewMode={viewMode}
            scopeId={scopeId}
            field={ConfigurationRuleSetupType.IsEnabled}
            disabled={disabled}
          />
        );
      },
    },
  };
};

export function getScopeErrorText(
  priority: number,
  error: boolean,
  entityType: ConfigurationRuleScopeEntityType,
): React.ReactNode | undefined {
  if (!error) return undefined;
  const reasons: string[] = [];
  const entityTitle = scopeEntityTitle[entityType].toLowerCase();

  if (!isMainScopePriority(priority)) {
    reasons.push(`this ${entityTitle} is not included in general configuration`);
  }

  if (entityType === ConfigurationRuleScopeEntityType.Stores) {
    reasons.push('store type of this store is not in scope');
    reasons.push('region of this store is not in scope');
  } else if (entityType === ConfigurationRuleScopeEntityType.StoreTypes) {
    reasons.push('all stores in this store type have disabled region');
  }

  return (
    <Column>
      This {entityTitle} is not valid. Possible reasons:
      {reasons.length > 0 && (
        <ul>
          {reasons.map(r => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </Column>
  );
}

export function getScopeStoreAvailabilityWarningText(
  warning: boolean,
  entityType: ConfigurationRuleScopeEntityType,
): React.ReactNode | undefined {
  if (!warning) return undefined;

  switch (entityType) {
    case ConfigurationRuleScopeEntityType.Regions:
      return 'All stores in this selected region are excluded by the general configuration scope.';
    case ConfigurationRuleScopeEntityType.StoreTypes:
      return 'All stores in this selected store type are excluded by the general configuration scope.';
    case ConfigurationRuleScopeEntityType.Stores:
    case ConfigurationRuleScopeEntityType.Departments:
      return undefined;
  }
}
